import AddIcon from "@mui/icons-material/Add";
import Can, { AbilityContext } from "../../casl/Can";
import DataTable from "../shared/table";
import Header from "../shared/header";
import LinkButton from "../shared/link-button";
import React, {
  FunctionComponent,
  useContext,
  useEffect,
  useState,
} from "react";
import { Box, TextField, InputAdornment, useTheme } from "@mui/material";
import { columns } from "./users.columns";
import { useFetchUsers } from "../../api/users";
import { transformFilterItemToFilterObject } from "../../utils/helper";
import {
  GridFilterItem,
  GridPaginationModel,
  GridSortItem,
} from "@mui/x-data-grid";
import useSearchStore from "../../stores/store-query-filters";
import SearchIcon from "@mui/icons-material/Search";
import UsersBreadcrumb from "../shared/users-breadcrumb";
import { STAFF_ROLES } from "../../constants/users-list-roles";

interface UsersListProps { }

/**
 * UsersList is a React component that displays a paginated list of users in a data table.
 * It includes functionality for filtering, sorting, and pagination.
 * 
 * @component
 * @returns {JSX.Element} A component that renders a list of users with CRUD operations
 * 
 * @features
 * - Pagination
 * - Filtering
 * - Sorting
 * - Create user button (with permission check)
 * - Data table display
 */
const UsersList: FunctionComponent<UsersListProps> = () => {
  const ability = useContext(AbilityContext);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  // Filter
const [filters, setFilters] = useState<Record<string, any>>({ role: STAFF_ROLES });


  // Search
  const searchKey = 'users';
  const searchQuery = useSearchStore((state) => state.getSearch(searchKey));
  const setSearch = useSearchStore((state) => state.setSearch);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(searchKey, event.target.value);
    setCurrentPage(1);
  };

  // Sort
  const [sort, setSort] = useState({});

  // Get Users list
  const {
    data: usersData,
    isLoading,
    isError,
  } = useFetchUsers(itemsPerPage, currentPage, searchQuery, filters, sort, 1);
  const users = usersData?.data;
  const userTotalItems = usersData?.pager?.total;

  // useEffect(() => {
  //   refetch();
  // }, [currentPage, refetch]);

  const handlePageChange = (newPage: GridPaginationModel) => {
    setCurrentPage(newPage.page + 1);
    setItemsPerPage(newPage.pageSize);
  };

  const handleFilterModelChange = (filterItem: GridFilterItem) => {
    const filterObject = transformFilterItemToFilterObject(filterItem);
    setFilters({ ...filterObject, role: STAFF_ROLES });
  };

  const handleSortModelChange = (sortItem: GridSortItem) => {
    setSort(sortItem);
  };

  const theme = useTheme();

  return (
    <>
      {!isError && (
        <Box sx={{ marginTop: "60px" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            gap={2}
            flexDirection={{ xs: "column", lg: "row" }}
            alignItems={{ xs: "flex-start", lg: "flex-end" }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection={{ xs: "column" }}
              alignItems={{ xs: "flex-start" }}>
              <Header title="Benutzer" />
              <UsersBreadcrumb />
            </Box>

            <Can I="create" a="User">
              <LinkButton
                label="Benutzer erstellen"
                startIcon={<AddIcon />}
                to="/users/create"
              />
            </Can>
          </Box>
          {(ability.can("view", "All Users") || ability.can("view", "Assigned Users")) && (
            <Box marginTop="10px" display="flex" flexDirection="column" height="70%">
              <Box sx={{ width: { xs: "100%", md: "400px" }, mb: 1 }}>
                <TextField
                  fullWidth
                  placeholder="Suchen..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: "200px",
                    maxWidth: "300px",
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "transparent",
                      borderRadius: "20px",
                      border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'}`,
                    },
                    "& .MuiOutlinedInput-input": {
                      color: "inherit",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                />
              </Box>
              <DataTable
                defaultSortField="id"
                defaultSortDirection="asc"
                parentPath="users"
                data={users ?? []}
                fieldsNames={columns}
                minHeight={500}
                onPageChange={handlePageChange}
                currentPage={currentPage - 1}
                totalItems={userTotalItems}
                itemsPerPage={itemsPerPage}
                ssrFilter={true}
                isLoading={isLoading}
                onFilterChange={handleFilterModelChange}
                onSortChange={handleSortModelChange}
                ssrSort={true}
              />
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default UsersList;